import { useEffect, useMemo } from "react";

import { APIEditorialComponent } from "@companion-core/shared/app/Interfaces/API";
import { useEditorialTemplateProps } from "@companion-core/shared/app/Components/DetailsPages/EditorialDetailsPage/EditorialDetailsPage.model";
import { trackDynamicPage } from "@companion-core/shared/app/tracking/dynamicPage";
import { getContentComponentMediaUrl } from "@companion-core/shared/app/Utils/componentHelper";
import { isMobile } from "@companion-core/shared/app/Utils/platform";
import {
  getDynamicComponentId,
  getDynamicComponentVendorId,
} from "@companion-core/shared/app/Utils/tracking";
import { useDetailsPage } from "@companion-core/shared/app/Components/DetailsPages/hooks/detailsPage";
import { usePersonalizeField } from "@companion-core/shared/app/Hooks/personalization";

export const useEditorialTemplate = ({
  data,
  pageSlug,
  isMobileSize,
}: useEditorialTemplateProps) => {
  const { t, handleScroll, lang } = useDetailsPage();
  const { personalizeField } = usePersonalizeField();
  const bannerImageUrl: string | undefined = useMemo(() => {
    if (!data) return undefined;

    const getImageUrl = () => {
      if (isMobileSize || isMobile()) {
        return (
          getContentComponentMediaUrl(data, "image") ?? getContentComponentMediaUrl(data, "detail")
        );
      }
      return (
        getContentComponentMediaUrl(data, "detail") ?? getContentComponentMediaUrl(data, "image")
      );
    };

    return getImageUrl();
  }, [data]);

  const editorialTitle = useMemo(
    () => personalizeField(data?.title) ?? "",
    [data, personalizeField]
  );
  const editorialSubtitle = useMemo(
    () => personalizeField(data?.subtitle) ?? "",
    [data, personalizeField]
  );
  const editorialDescription = useMemo(
    () => personalizeField(data?.description) ?? "",
    [data, personalizeField]
  );
  const editorialContentLists = useMemo(
    () => (data as APIEditorialComponent | undefined)?.content_lists ?? [],
    [data]
  );
  const editorialType = useMemo(() => data?.type ?? [], [data]);

  useEffect(() => {
    if (data) {
      trackDynamicPage({
        pageSlug: pageSlug,
        contentId: getDynamicComponentId(data),
        vendorId: getDynamicComponentVendorId(data),
      });
    }
  }, [data]);

  return {
    t,
    handleScroll,
    lang,
    isMobileSize,
    bannerImageUrl,
    editorialTitle,
    editorialSubtitle,
    editorialDescription,
    editorialContentLists,
    editorialType,
  };
};
